import * as THREE from 'three';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import type { DevConfig } from '../../devConfig';
import type { CameraConfig } from '../../cameraConfig';
import type { Sight } from '../../types';
import type { ColliderDef, Vector3Like } from '../collision/colliderTypes';

const DEBUG_RENDER_ORDER = 10;

export const ORBIT_CENTER_GIZMO_ID = '__orbit-center__';

const COLLIDER_COLORS = {
  box: 0x00ff00,
  cylinder: 0x0066ff,
  sphere: 0xff6600,
  disabled: 0x666666,
} as const;

export class DevVisuals {
  private readonly scene: THREE.Scene;
  private readonly isDevMode: boolean;
  private sightDebugMarkers: THREE.Object3D[] = [];
  private colliderWireframes: Map<string, THREE.Group> = new Map();
  private gridFloor: THREE.GridHelper | null = null;
  private gridCeil: THREE.GridHelper | null = null;
  private orbitCenterMarker: THREE.Mesh | null = null;
  private currentDevConfig: DevConfig | null = null;
  private gizmo: TransformControls | null = null;
  private gizmoHelper: THREE.Object3D | null = null;
  private gizmoTargetId: string | null = null;
  private onGizmoPositionChange: ((id: string, position: Vector3Like) => void) | null = null;
  private onGizmoDraggingChanged: ((dragging: boolean) => void) | null = null;
  private onGizmoRenderRequest: (() => void) | null = null;

  constructor(scene: THREE.Scene, isDevMode: boolean) {
    this.scene = scene;
    this.isDevMode = isDevMode;
  }

  addSightDebugMarkers(allSights: Sight[]): void {
    if (!this.isDevMode || allSights.length === 0 || this.sightDebugMarkers.length > 0) {
      return;
    }
    const markerGeometry = new THREE.SphereGeometry(0.02, 12, 12);
    const markerMaterial = new THREE.MeshBasicMaterial({
      color: 0xff00ff,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
      depthTest: false,
    });

    allSights.forEach((sight) => {
      const position = sight.position;
      if (!position) {
        return;
      }
      const marker = new THREE.Mesh(markerGeometry, markerMaterial);
      marker.name = `sight-marker-${sight.id}`;
      marker.position.set(position.x, position.y, position.z);
      marker.renderOrder = DEBUG_RENDER_ORDER;
      marker.visible = this.shouldShowSightMarkers();
      this.sightDebugMarkers.push(marker);
      this.scene.add(marker);
    });
  }

  updateGridHeights(config: CameraConfig): void {
    if (this.gridFloor) {
      this.gridFloor.position.y = config.clampMinHeight;
    }
    if (this.gridCeil) {
      this.gridCeil.position.y = config.clampMaxHeight;
    }
  }

  updateColliderVisuals(colliders: ColliderDef[]): void {
    if (!this.isDevMode) {
      return;
    }

    const visible = this.shouldShowCollisionBounds();
    const activeIds = new Set(colliders.map((c) => c.id));

    // Remove wireframes for colliders that no longer exist
    for (const [id, group] of this.colliderWireframes) {
      if (!activeIds.has(id)) {
        this.scene.remove(group);
        this.disposeColliderGroup(group);
        this.colliderWireframes.delete(id);
      }
    }

    for (const collider of colliders) {
      const existing = this.colliderWireframes.get(collider.id);
      if (existing) {
        this.updateWireframeTransform(existing, collider);
        this.updateWireframeColor(existing, collider);
        existing.visible = visible;
      } else {
        const wireframe = this.createColliderWireframe(collider);
        wireframe.visible = visible;
        this.colliderWireframes.set(collider.id, wireframe);
        this.scene.add(wireframe);
      }
    }
  }

  applyDevConfig(config: DevConfig): void {
    this.currentDevConfig = config;
    if (!this.isDevMode) {
      return;
    }
    const collisionVisible = this.shouldShowCollisionBounds();
    for (const wireframe of this.colliderWireframes.values()) {
      wireframe.visible = collisionVisible;
    }
    if (this.gizmo && this.gizmoHelper) {
      const gizmoVisible = this.gizmoTargetId === ORBIT_CENTER_GIZMO_ID
        ? this.shouldShowOrbitCenterMarker()
        : collisionVisible && this.gizmoTargetId !== null;
      this.gizmoHelper.visible = gizmoVisible;
      this.gizmo.enabled = gizmoVisible;
    }
    const sightMarkersVisible = this.shouldShowSightMarkers();
    this.sightDebugMarkers.forEach((object) => {
      object.visible = sightMarkersVisible;
    });
    const gridVisible = this.shouldShowGrid();
    if (gridVisible) {
      this.ensureDebugGridHelpers();
    }
    if (this.gridFloor) {
      this.gridFloor.visible = gridVisible;
    }
    if (this.gridCeil) {
      this.gridCeil.visible = gridVisible;
    }
    const orbitMarkerVisible = this.shouldShowOrbitCenterMarker();
    if (orbitMarkerVisible) {
      this.ensureOrbitCenterMarker();
    }
    if (this.orbitCenterMarker) {
      this.orbitCenterMarker.visible = orbitMarkerVisible;
    }
  }

  initGizmo(
    camera: THREE.Camera,
    domElement: HTMLElement,
    onPositionChange: (id: string, position: Vector3Like) => void,
    onDraggingChanged: (dragging: boolean) => void,
    onRenderRequest: () => void,
  ): void {
    if (!this.isDevMode || this.gizmo) {
      return;
    }

    this.onGizmoPositionChange = onPositionChange;
    this.onGizmoDraggingChanged = onDraggingChanged;
    this.onGizmoRenderRequest = onRenderRequest;

    this.gizmo = new TransformControls(camera, domElement);
    this.gizmo.setMode('translate');
    this.gizmo.setSize(0.5);
    this.gizmoHelper = this.gizmo.getHelper();
    this.gizmoHelper.visible = false;
    this.scene.add(this.gizmoHelper);

    this.gizmo.addEventListener('change', () => {
      this.onGizmoRenderRequest?.();
    });

    this.gizmo.addEventListener('objectChange', () => {
      if (!this.gizmoTargetId || !this.gizmo?.object) {
        return;
      }
      const pos = this.gizmo.object.position;
      this.onGizmoPositionChange?.(this.gizmoTargetId, {
        x: pos.x,
        y: pos.y,
        z: pos.z,
      });
    });

    this.gizmo.addEventListener('dragging-changed', (event: { value: unknown }) => {
      this.onGizmoDraggingChanged?.(event.value as boolean);
    });
  }

  setGizmoTarget(targetId: string | null): void {
    if (!this.gizmo) {
      return;
    }

    // Toggle off if same id
    if (targetId === null || targetId === this.gizmoTargetId) {
      this.gizmo.detach();
      if (this.gizmoHelper) {
        this.gizmoHelper.visible = false;
      }
      this.gizmoTargetId = null;
      this.onGizmoRenderRequest?.();
      return;
    }

    // Orbit center marker
    if (targetId === ORBIT_CENTER_GIZMO_ID) {
      if (!this.orbitCenterMarker) {
        return;
      }
      this.gizmo.attach(this.orbitCenterMarker);
      if (this.gizmoHelper) {
        this.gizmoHelper.visible = this.shouldShowOrbitCenterMarker();
      }
      this.gizmoTargetId = targetId;
      this.onGizmoRenderRequest?.();
      return;
    }

    // Collider wireframe
    const wireframe = this.colliderWireframes.get(targetId);
    if (!wireframe) {
      return;
    }

    this.gizmo.attach(wireframe);
    if (this.gizmoHelper) {
      this.gizmoHelper.visible = this.shouldShowCollisionBounds();
    }
    this.gizmoTargetId = targetId;
    this.onGizmoRenderRequest?.();
  }

  disposeGizmo(): void {
    if (this.gizmo) {
      this.gizmo.detach();
      if (this.gizmoHelper) {
        this.scene.remove(this.gizmoHelper);
        this.gizmoHelper = null;
      }
      this.gizmo.dispose();
      this.gizmo = null;
      this.gizmoTargetId = null;
    }
  }

  ensureOrbitCenterMarker(): void {
    if (!this.isDevMode || this.orbitCenterMarker) {
      return;
    }
    const geometry = new THREE.SphereGeometry(0.02, 16, 16);
    const material = new THREE.MeshBasicMaterial({
      color: 0x00ffff,
      transparent: true,
      opacity: 0.85,
      depthTest: false,
      depthWrite: false,
    });
    this.orbitCenterMarker = new THREE.Mesh(geometry, material);
    this.orbitCenterMarker.renderOrder = DEBUG_RENDER_ORDER;
    this.orbitCenterMarker.visible = false;
    this.scene.add(this.orbitCenterMarker);
  }

  syncOrbitCenterMarker(liveTarget: THREE.Vector3): void {
    if (this.orbitCenterMarker?.visible) {
      this.orbitCenterMarker.position.copy(liveTarget);
    }
  }

  get currentGizmoTargetId(): string | null {
    return this.gizmoTargetId;
  }

  setOrbitCenter(target: THREE.Vector3): void {
    if (!this.isDevMode) {
      return;
    }
    this.ensureOrbitCenterMarker();
    if (this.orbitCenterMarker) {
      this.orbitCenterMarker.position.copy(target);
    }
  }

  disposeOrbitCenter(): void {
    if (this.orbitCenterMarker) {
      this.scene.remove(this.orbitCenterMarker);
      this.orbitCenterMarker.geometry.dispose();
      if (Array.isArray(this.orbitCenterMarker.material)) {
        this.orbitCenterMarker.material.forEach((material) => material.dispose());
      } else {
        this.orbitCenterMarker.material.dispose();
      }
      this.orbitCenterMarker = null;
    }
  }

  private createColliderWireframe(collider: ColliderDef): THREE.Group {
    const baseGeometry = this.createColliderGeometry(collider);
    const edgesGeometry = new THREE.EdgesGeometry(baseGeometry);

    const color = collider.enabled
      ? COLLIDER_COLORS[collider.type]
      : COLLIDER_COLORS.disabled;
    const enabled = collider.enabled;

    // Layer 1: translucent filled mesh — gives volume cue
    const fillMaterial = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: enabled ? 0.12 : 0.05,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    const fillMesh = new THREE.Mesh(baseGeometry, fillMaterial);
    fillMesh.name = 'collider-fill';
    fillMesh.renderOrder = DEBUG_RENDER_ORDER - 1;

    // Layer 2: back wireframe — drawn through geometry, dim (x-ray feel)
    const backMaterial = new THREE.LineBasicMaterial({
      color,
      transparent: true,
      opacity: enabled ? 0.25 : 0.12,
      depthTest: false,
      depthWrite: false,
    });
    const backWire = new THREE.LineSegments(edgesGeometry, backMaterial);
    backWire.name = 'collider-wire-back';
    backWire.renderOrder = DEBUG_RENDER_ORDER;

    // Layer 3: front wireframe — depth-tested, bright (clear near edges)
    const frontMaterial = new THREE.LineBasicMaterial({
      color,
      transparent: true,
      opacity: enabled ? 1.0 : 0.5,
      depthTest: true,
      depthWrite: false,
    });
    const frontWire = new THREE.LineSegments(edgesGeometry, frontMaterial);
    frontWire.name = 'collider-wire-front';
    frontWire.renderOrder = DEBUG_RENDER_ORDER + 1;

    const group = new THREE.Group();
    group.name = `collider-wireframe-${collider.id}`;
    group.position.set(collider.position.x, collider.position.y, collider.position.z);
    if (collider.type === 'box' && collider.rotation) {
      group.rotation.set(collider.rotation.x, collider.rotation.y, collider.rotation.z);
    }
    group.userData.shapeKey = this.getShapeKey(collider);
    group.add(fillMesh, backWire, frontWire);

    return group;
  }

  private disposeColliderGroup(group: THREE.Group): void {
    const fillMesh = group.getObjectByName('collider-fill') as THREE.Mesh | null;
    const backWire = group.getObjectByName('collider-wire-back') as THREE.LineSegments | null;
    const frontWire = group.getObjectByName('collider-wire-front') as THREE.LineSegments | null;

    fillMesh?.geometry.dispose();
    (fillMesh?.material as THREE.Material | undefined)?.dispose();
    // backWire and frontWire share the same EdgesGeometry — dispose once
    backWire?.geometry.dispose();
    (backWire?.material as THREE.Material | undefined)?.dispose();
    (frontWire?.material as THREE.Material | undefined)?.dispose();
  }

  private createColliderGeometry(collider: ColliderDef): THREE.BufferGeometry {
    if (collider.type === 'box') {
      return new THREE.BoxGeometry(collider.size.x, collider.size.y, collider.size.z);
    }
    if (collider.type === 'sphere') {
      return new THREE.SphereGeometry(collider.radius, 24, 16);
    }
    // Open-ended cylinder (no caps) with 32 segments
    return new THREE.CylinderGeometry(
      collider.radius,
      collider.radius,
      collider.height,
      32,
      1,
      true,
    );
  }

  private updateWireframeTransform(group: THREE.Group, collider: ColliderDef): void {
    group.position.set(collider.position.x, collider.position.y, collider.position.z);
    if (collider.type === 'box' && collider.rotation) {
      group.rotation.set(collider.rotation.x, collider.rotation.y, collider.rotation.z);
    } else {
      group.rotation.set(0, 0, 0);
    }

    // Only rebuild geometry when shape parameters actually change
    const shapeKey = this.getShapeKey(collider);
    if (group.userData.shapeKey === shapeKey) {
      return;
    }
    group.userData.shapeKey = shapeKey;

    const fillMesh = group.getObjectByName('collider-fill') as THREE.Mesh | null;
    const backWire = group.getObjectByName('collider-wire-back') as THREE.LineSegments | null;
    const frontWire = group.getObjectByName('collider-wire-front') as THREE.LineSegments | null;

    const oldBaseGeom = fillMesh?.geometry;
    const oldEdgesGeom = backWire?.geometry; // shared with frontWire

    const newBaseGeom = this.createColliderGeometry(collider);
    const newEdgesGeom = new THREE.EdgesGeometry(newBaseGeom);

    if (fillMesh) fillMesh.geometry = newBaseGeom;
    if (backWire) backWire.geometry = newEdgesGeom;
    if (frontWire) frontWire.geometry = newEdgesGeom;

    oldBaseGeom?.dispose();
    oldEdgesGeom?.dispose();
  }

  private getShapeKey(collider: ColliderDef): string {
    if (collider.type === 'box') {
      return `box-${collider.size.x}-${collider.size.y}-${collider.size.z}`;
    }
    if (collider.type === 'sphere') {
      return `sphere-${collider.radius}`;
    }
    return `cyl-${collider.radius}-${collider.height}`;
  }

  private updateWireframeColor(group: THREE.Group, collider: ColliderDef): void {
    const targetColor = collider.enabled
      ? COLLIDER_COLORS[collider.type]
      : COLLIDER_COLORS.disabled;
    const enabled = collider.enabled;

    for (const child of group.children) {
      const material = (child as THREE.Mesh | THREE.LineSegments).material as
        | THREE.MeshBasicMaterial
        | THREE.LineBasicMaterial
        | undefined;
      if (!material) continue;
      material.color.setHex(targetColor);
      if (child.name === 'collider-fill') {
        material.opacity = enabled ? 0.12 : 0.05;
      } else if (child.name === 'collider-wire-back') {
        material.opacity = enabled ? 0.25 : 0.12;
      } else if (child.name === 'collider-wire-front') {
        material.opacity = enabled ? 1.0 : 0.5;
      }
    }
  }

  private shouldShowCollisionBounds(): boolean {
    return Boolean(this.isDevMode && this.currentDevConfig?.showCollisionBounds);
  }

  private shouldShowSightMarkers(): boolean {
    return Boolean(this.isDevMode && this.currentDevConfig?.showSightMarkers);
  }

  private shouldShowGrid(): boolean {
    return Boolean(this.isDevMode && this.currentDevConfig?.showReferenceGrid);
  }

  private shouldShowOrbitCenterMarker(): boolean {
    return Boolean(this.isDevMode && this.currentDevConfig?.showOrbitCenterMarker);
  }

  private ensureDebugGridHelpers(): void {
    if (!this.isDevMode || this.gridFloor || this.gridCeil) {
      return;
    }
    this.gridFloor = new THREE.GridHelper(10, 10);
    this.gridCeil = new THREE.GridHelper(10, 10);
    this.gridFloor.renderOrder = DEBUG_RENDER_ORDER;
    this.gridCeil.renderOrder = DEBUG_RENDER_ORDER;
    this.scene.add(this.gridFloor, this.gridCeil);
  }
}
